/**
 * Tableau des actifs pris en compte par un agrégateur — repris du client.
 *
 * Il attend une liste plate avec `statut`, `revenu` et `raison_exclusion`. Notre
 * API rend `{retenus, ecartes}` : la conversion vit dans la page, dans une
 * fonction nommée, pour que ce composant reste identique au sien.
 */
import { useState } from 'react';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Badge } from '@/shared/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/shared/ui/dialog';
import { Sliders, EyeOff, Eye, Save, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import MentionEstimation from '@/shared/components/MentionEstimation';

const fmtEUR = (v) =>
  v == null
    ? '—'
    : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

const STATUT_BADGE = {
  actif: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  en_attente: 'bg-amber-50 text-amber-700 border-amber-200',
  exclu: 'bg-red-50 text-red-700 border-red-200',
};
const STATUT_LABEL = { actif: 'Actif', en_attente: 'En attente', exclu: 'Exclu' };

/**
 * Composant standardisé "Actifs pris en compte dans le calcul".
 * Utilisé par Symphonics, Survoltage et tout futur agrégateur.
 *
 * Props:
 *  - aggregator: 'Symphonics' | 'Survoltage' | ...
 *  - assets: Array from GET /api/{aggregator.toLowerCase()}/assets
 *  - accentColor: tailwind text-* class (ex 'text-cyan-700')
 *  - badgeClass: tailwind classes for the aggregator badge
 *  - filterAggregator (bool, default true): filtrer sur `aggregateur === aggregator` (Symphonics uniquement)
 *  - isAdmin: bool — active les boutons personnaliser/exclure
 *  - onCustomize(asset): callback ouverture dialog paramètres (Symphonics uniquement)
 *  - onRefresh(): callback après un toggle exclusion pour recharger la liste
 */
export default function AggregatorAssetsTable({
  aggregator,
  assets,
  accentColor = 'text-neutral-700',
  badgeClass = 'bg-neutral-600 hover:bg-neutral-700 text-white',
  filterAggregator = true,
  isAdmin = false,
  onCustomize,
  onRefresh,
  supportsCustomization = false,
}) {
  const [typeFilter, setTypeFilter] = useState(null);
  const [statutFilter, setStatutFilter] = useState('all');
  const [excludeDialog, setExcludeDialog] = useState(null); // { asset, reason }
  const [saving, setSaving] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  const norm = (t) => (t === 'ECS' ? 'Ballon' : t);
  const filtered = (assets || []).filter((a) => {
    if (filterAggregator && a.aggregateur !== aggregator) return false;
    if (typeFilter && norm(a.type) !== typeFilter) return false;
    if (statutFilter !== 'all' && a.statut !== statutFilter) return false;
    return true;
  });

  const totalPris = (assets || []).filter(
    (a) => (!filterAggregator || a.aggregateur === aggregator) && a.statut === 'actif'
  ).length;
  const allTypes = [...new Set((assets || []).map((a) => norm(a.type)).filter(Boolean))];

  const doExclude = async () => {
    if (!excludeDialog) return;
    setSaving(true);
    try {
      await api.post(`/aggregator-assets/${excludeDialog.asset.id}/toggle-exclusion`, {
        excluded: true,
        reason: excludeDialog.reason || 'Exclu manuellement',
      });
      toast.success('Actif exclu du calcul');
      setExcludeDialog(null);
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const doInclude = async (asset) => {
    if (!window.confirm(`Réintégrer cet actif ${asset.type} dans le calcul ?`)) return;
    try {
      await api.post(`/aggregator-assets/${asset.id}/toggle-exclusion`, { excluded: false });
      toast.success('Actif réintégré au calcul');
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  return (
    <div
      className="rounded-lg border border-neutral-200/80 bg-white overflow-hidden"
      data-testid={`aggregator-assets-table-${aggregator.toLowerCase()}`}
    >
      <div className="px-5 py-3 border-b border-neutral-100 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-start gap-2 min-w-0">
          <button
            onClick={() => setCollapsed((c) => !c)}
            className="mt-0.5 p-1 rounded hover:bg-neutral-100 text-neutral-500 hover:text-neutral-900 flex-shrink-0"
            aria-label={collapsed ? 'Développer' : 'Réduire'}
            data-testid={`assets-collapse-${aggregator.toLowerCase()}`}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          <div>
            <h2 className="text-[13px] font-medium text-neutral-900">Actifs pris en compte dans le calcul</h2>
            <p className="text-[11px] text-neutral-400 mt-0.5">
              Uniquement les actifs sous mandat <span className={`font-medium ${accentColor}`}>{aggregator}</span>
              {supportsCustomization && ' — Cliquez sur Personnaliser pour définir des paramètres spécifiques'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!collapsed && (
            <>
              <select
                value={typeFilter || 'all'}
                onChange={(e) => setTypeFilter(e.target.value === 'all' ? null : e.target.value)}
                className="h-8 px-2 text-[11px] border border-neutral-200 rounded-md"
                data-testid={`assets-type-filter-${aggregator.toLowerCase()}`}
              >
                <option value="all">Tous types</option>
                {allTypes.map((t) => (
                  <option key={t} value={t}>
                    {t === 'Ballon' ? 'Ballon ECS' : t}
                  </option>
                ))}
              </select>
              <select
                value={statutFilter}
                onChange={(e) => setStatutFilter(e.target.value)}
                className="h-8 px-2 text-[11px] border border-neutral-200 rounded-md"
                data-testid={`assets-statut-filter-${aggregator.toLowerCase()}`}
              >
                <option value="all">Tous statuts</option>
                <option value="actif">Actifs</option>
                <option value="en_attente">En attente</option>
                <option value="exclu">Exclus</option>
              </select>
            </>
          )}
          <Badge variant="outline" className="text-[10px] bg-emerald-50 border-emerald-200 text-emerald-700" data-testid={`assets-total-inclus-${aggregator.toLowerCase()}`}>
            {totalPris} pris en compte
          </Badge>
          {!collapsed && (
            <Badge variant="outline" className="text-[10px] bg-neutral-50">
              {filtered.length} affiché{filtered.length > 1 ? 's' : ''}
            </Badge>
          )}
        </div>
      </div>

      {!collapsed && (
      <div className="max-h-[520px] overflow-y-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-white z-10">
            <TableRow>
              <TableHead className="w-[90px]">Agrégateur</TableHead>
              <TableHead>Client (réf. chantier)</TableHead>
              <TableHead className="w-[100px]">Type</TableHead>
              <TableHead className="w-[80px] text-right">Puissance</TableHead>
              <TableHead className="w-[100px] text-right">Mensuel estimé</TableHead>
              <TableHead className="w-[100px] text-right">Annuel estimé</TableHead>
              <TableHead className="w-[110px]">Statut</TableHead>
              {supportsCustomization && <TableHead className="w-[110px]">Params</TableHead>}
              {isAdmin && <TableHead className="w-[160px] text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={supportsCustomization ? (isAdmin ? 9 : 8) : (isAdmin ? 8 : 7)}
                  className="text-center text-[12px] text-neutral-400 py-6"
                  data-testid={`assets-empty-${aggregator.toLowerCase()}`}
                >
                  Aucun actif ne correspond aux filtres pour {aggregator}.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((a) => {
              const rev = a.revenu || {};
              const statut = a.statut || 'actif';
              return (
                <TableRow key={a.id} data-testid={`asset-row-${a.id}`}>
                  <TableCell>
                    <Badge className={`${badgeClass} text-[10px]`}>{aggregator}</Badge>
                  </TableCell>
                  <TableCell className="text-[12px] font-mono text-neutral-700" data-testid={`asset-refchantier-${a.id}`}>
                    {a.reference_chantier || '—'}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px]">
                      {a.type === 'ECS' ? 'Ballon ECS' : (a.type === 'Ballon' ? 'Ballon ECS' : a.type)}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right text-[12px] text-neutral-600">
                    {a.puissance_kw ? `${Number(a.puissance_kw).toFixed(1)} kW` : '—'}
                  </TableCell>
                  <TableCell className="text-right text-[12px] font-medium">
                    {statut === 'actif' ? fmtEUR(rev.mensuel) : <span className="text-neutral-300">—</span>}
                  </TableCell>
                  <TableCell className="text-right text-[12px] text-neutral-600">
                    {statut === 'actif' ? fmtEUR(rev.annuel) : <span className="text-neutral-300">—</span>}
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-0.5">
                      <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border w-fit ${STATUT_BADGE[statut] || STATUT_BADGE.actif}`} data-testid={`asset-statut-${a.id}`}>
                        {STATUT_LABEL[statut] || 'Actif'}
                      </span>
                      {statut !== 'actif' && a.raison_exclusion && (
                        <span className="text-[9.5px] text-neutral-400 italic">{a.raison_exclusion}</span>
                      )}
                    </div>
                  </TableCell>
                  {supportsCustomization && (
                    <TableCell>
                      {a.symphonics_params ? (
                        <Badge className="bg-violet-100 text-violet-800 border-violet-200 text-[9px]">Personnalisé</Badge>
                      ) : (
                        <span className="text-[10px] text-neutral-400">Défaut type</span>
                      )}
                    </TableCell>
                  )}
                  {isAdmin && (
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        {supportsCustomization && onCustomize && (
                          <button
                            onClick={() => onCustomize(a)}
                            className="text-[11px] text-cyan-700 hover:text-cyan-900 font-medium inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-cyan-50"
                            data-testid={`asset-customize-${a.id}`}
                          >
                            <Sliders className="w-3 h-3" /> Personnaliser
                          </button>
                        )}
                        {a.excluded_from_revenue ? (
                          <button
                            onClick={() => doInclude(a)}
                            className="text-[11px] text-emerald-700 hover:text-emerald-900 font-medium inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-emerald-50"
                            data-testid={`asset-include-${a.id}`}
                          >
                            <Eye className="w-3 h-3" /> Réintégrer
                          </button>
                        ) : (
                          <button
                            onClick={() => setExcludeDialog({ asset: a, reason: '' })}
                            className="text-[11px] text-red-600 hover:text-red-800 font-medium inline-flex items-center gap-1 px-1.5 py-0.5 rounded hover:bg-red-50"
                            data-testid={`asset-exclude-${a.id}`}
                          >
                            <EyeOff className="w-3 h-3" /> Exclure
                          </button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      )}
      <MentionEstimation
        source={aggregator === 'Survoltage' ? 'survoltage' : 'symphonics'}
        bloc="tableau-actifs" className="mt-3" />

      {/* Dialog motif d'exclusion */}
      <Dialog open={!!excludeDialog} onOpenChange={(o) => { if (!o) setExcludeDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="exclude-dialog">
          <DialogHeader>
            <DialogTitle>Exclure cet actif du calcul</DialogTitle>
            <DialogDescription>
              L&apos;actif ne sera plus pris en compte dans l&apos;estimation {aggregator}. Vous pourrez le réintégrer à tout moment.
            </DialogDescription>
          </DialogHeader>
          {excludeDialog && (
            <div className="space-y-3">
              <div className="p-2.5 rounded-md bg-neutral-50 border border-neutral-200 text-[11.5px]">
                <p><strong>Type :</strong> {excludeDialog.asset.type}</p>
                <p><strong>Réf. chantier :</strong> {excludeDialog.asset.reference_chantier || '—'}</p>
                <p><strong>Puissance :</strong> {excludeDialog.asset.puissance_kw ? `${excludeDialog.asset.puissance_kw} kW` : '—'}</p>
              </div>
              <div>
                <Label className="text-[11px] text-neutral-500">Motif d&apos;exclusion (optionnel)</Label>
                <Input
                  className="h-9 mt-1"
                  placeholder="Ex : maintenance, hors périmètre, litige…"
                  value={excludeDialog.reason}
                  onChange={(e) => setExcludeDialog((s) => ({ ...s, reason: e.target.value }))}
                  data-testid="exclude-reason-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setExcludeDialog(null)}>Annuler</Button>
            <Button onClick={doExclude} disabled={saving} data-testid="exclude-confirm-btn">
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Enregistrement…' : 'Exclure'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
